// Lab Sample Context - Testing Planning & Tracking Module
// Lightweight tracking for test samples, results, and reporting
// NOT a full LIMS - focused on integration with FRACAS/8D and FMEA feedback
/* eslint-disable react-refresh/only-export-components */
// ============================================================

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
    collection,
    query,
    where,
    onSnapshot,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';
import { calculateLabMetrics } from '../utils/lab/labMetrics';

// Lab Sample Interface
export interface LabSample {
    id: string;
    sampleNumber: string;
    sampleType: 'test_unit' | 'field_trial' | 'warranty_return' | 'customer_sample' | 'validation';
    product: string;
    serialNumber?: string;
    batchLot?: string;

    // Submission Info
    submittedBy: string;
    submittedDate: string;
    receivedDate: string;
    source: string; // Manufacturing, Field, Customer, etc.

    // Linking to Other Modules
    fracasIncidentId?: string;
    eightDReportId?: string;
    pfmeaItemId?: string;  // Link to PFMEA failure mode
    dfmeaItemId?: string;  // Link to DFMEA failure mode

    // Test Planning
    priority: 'critical' | 'high' | 'medium' | 'low';
    requestedTests: string[];
    testScheduleDate?: string;
    testDueDate?: string;
    assignedLab?: string;
    assignedTechnician?: string;

    // Status & Progress
    status: 'received' | 'queued' | 'testing' | 'analysis' | 'complete' | 'on_hold' | 'cancelled';
    completionDate?: string;

    // Test Results & Reports
    testResults: TestResult[];
    failureModeConfirmed?: string;
    rootCauseFound?: boolean;
    recommendations?: string;

    // Documentation
    photos?: string[];
    reports?: AttachedReport[];
    notes?: string;

    // Metadata
    createdAt: string;
    updatedAt: string;
}

export interface TestResult {
    id: string;
    testType: string;
    testStandard?: string;
    performedBy: string;
    performedDate: string;
    result: 'pass' | 'fail' | 'inconclusive';
    findings: string;
    measurements?: { [key: string]: string | number };
    attachments?: string[];
}

export interface AttachedReport {
    id: string;
    fileName: string;
    fileType: string;
    uploadedBy: string;
    uploadedDate: string;
    reportType: 'test_results' | 'failure_analysis' | 'material_cert' | 'dimensional' | 'environmental' | 'other';
}

// Context State Interface
interface LabSampleContextState {
    samples: LabSample[];
    loading: boolean;
    error: string | null;
    selectedSampleId: string | null;

    // Actions
    addSample: (sample: Omit<LabSample, 'id' | 'sampleNumber' | 'createdAt' | 'updatedAt' | 'testResults'>) => void;
    updateSample: (id: string, updates: Partial<LabSample>) => void;
    deleteSample: (id: string) => void;
    selectSample: (id: string | null) => void;
    getSampleById: (id: string) => LabSample | undefined;
    getSamplesByStatus: (status: LabSample['status']) => LabSample[];
    getSamplesByPriority: (priority: LabSample['priority']) => LabSample[];
    addTestResult: (sampleId: string, result: Omit<TestResult, 'id'>) => void;
    linkToFracas: (sampleId: string, fracasId: string) => void;
    linkTo8D: (sampleId: string, eightDId: string) => void;
    linkToPFMEA: (sampleId: string, pfmeaId: string) => void;
    linkToDFMEA: (sampleId: string, dfmeaId: string) => void;
    getMetrics: () => {
        totalSamples: number;
        queuedSamples: number;
        criticalSamples: number;
        averageTurnaroundTime: number;
        samplesByStatus: Record<string, number>;
        samplesByType: Record<string, number>;
        testBacklog: number;
    };
}

const LabSampleContext = createContext<LabSampleContextState | undefined>(undefined);

export const LabSampleProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user, loading: authLoading } = useAuth();
    const [samples, setSamples] = useState<LabSample[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedSampleId, setSelectedSampleId] = useState<string | null>(null);

    // Real-time listener — loads all lab samples from Firestore
    useEffect(() => {
        // Wait for auth to fully resolve before subscribing.
        if (authLoading) return;

        if (!user) {
            setSamples([]);
            setLoading(false);
            return;
        }

        // autoGainAdmin sees ALL samples; other roles scope to their org
        const isAutoGainAdmin = user.role === 'autoGainAdmin';
        const q = (isAutoGainAdmin || !user.organizationId)
            ? query(collection(db, 'lab_samples'))
            : query(
                collection(db, 'lab_samples'),
                where('organizationId', '==', user.organizationId)
              );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedSamples: LabSample[] = snapshot.docs.map(d => ({
                ...d.data(),
                id: d.id,
            } as LabSample));
            
            // Sort client-side
            fetchedSamples.sort((a, b) => new Date(b.createdAt || Date.now()).getTime() - new Date(a.createdAt || Date.now()).getTime());
            
            setSamples(fetchedSamples);
            setLoading(false);
        }, (err) => {
            console.error('Failed to subscribe to lab_samples:', err);
            setError('Failed to load lab samples from database.');
            setLoading(false);
        });
        return () => unsubscribe();
    }, [authLoading, user?.uid, user?.role, user?.organizationId]);

    const addSample = async (sampleData: Omit<LabSample, 'id' | 'sampleNumber' | 'createdAt' | 'updatedAt' | 'testResults'>) => {
        if (!user?.organizationId) {
            throw new Error("User must be authenticated and have an organization ID.");
        }
        const newSample = {
            ...sampleData,
            organizationId: user.organizationId,
            sampleNumber: `${sampleData.sampleType.substring(0, 2).toUpperCase()}-${new Date().getFullYear()}-${String(Date.now()).slice(-4)}`,
            testResults: [],
            createdAt: Timestamp.fromDate(new Date()),
            updatedAt: Timestamp.fromDate(new Date()),
        };
        try {
            await addDoc(collection(db, 'lab_samples'), newSample);
        } catch (err) {
            console.error('Error adding lab sample:', err);
            setError('Failed to create lab sample.');
        }
    };

    const updateSample = async (id: string, updates: Partial<LabSample>) => {
        try {
            const ref = doc(db, 'lab_samples', id);
            await updateDoc(ref, { ...updates, updatedAt: Timestamp.fromDate(new Date()) });
        } catch (err) {
            console.error('Error updating lab sample:', err);
            setError('Failed to update lab sample.');
        }
    };

    const deleteSample = async (id: string) => {
        try {
            await deleteDoc(doc(db, 'lab_samples', id));
        } catch (err) {
            console.error('Error deleting lab sample:', err);
            setError('Failed to delete lab sample.');
        }
    };

    const selectSample = (id: string | null) => {
        setSelectedSampleId(id);
    };

    const getSampleById = (id: string): LabSample | undefined => {
        return samples.find(sample => sample.id === id);
    };

    const getSamplesByStatus = (status: LabSample['status']): LabSample[] => {
        return samples.filter(sample => sample.status === status);
    };

    const getSamplesByPriority = (priority: LabSample['priority']): LabSample[] => {
        return samples.filter(sample => sample.priority === priority);
    };

    const addTestResult = async (sampleId: string, result: Omit<TestResult, 'id'>) => {
        const sample = getSampleById(sampleId);
        if (!sample) return;
        const newResult: TestResult = {
            ...result,
            id: `TR-${String((sample.testResults?.length || 0) + 1).padStart(3, '0')}`,
        };
        await updateSample(sampleId, {
            testResults: [...(sample.testResults || []), newResult],
        });
    };

    const linkToFracas = (sampleId: string, fracasId: string) => {
        updateSample(sampleId, { fracasIncidentId: fracasId });
    };

    const linkTo8D = (sampleId: string, eightDId: string) => {
        updateSample(sampleId, { eightDReportId: eightDId });
    };

    const linkToPFMEA = (sampleId: string, pfmeaId: string) => {
        updateSample(sampleId, { pfmeaItemId: pfmeaId });
    };

    const linkToDFMEA = (sampleId: string, dfmeaId: string) => {
        updateSample(sampleId, { dfmeaItemId: dfmeaId });
    };

    const getMetrics = () => calculateLabMetrics(samples);

    const value: LabSampleContextState = {
        samples,
        loading,
        error,
        selectedSampleId,
        addSample,
        updateSample,
        deleteSample,
        selectSample,
        getSampleById,
        getSamplesByStatus,
        getSamplesByPriority,
        addTestResult,
        linkToFracas,
        linkTo8D,
        linkToPFMEA,
        linkToDFMEA,
        getMetrics,
    };

    return <LabSampleContext.Provider value={value}>{children}</LabSampleContext.Provider>;
};

// Custom hook to use Lab Sample context
export const useLabSample = (): LabSampleContextState => {
    const context = useContext(LabSampleContext);
    if (!context) {
        throw new Error('useLabSample must be used within a LabSampleProvider');
    }
    return context;
};

export default LabSampleContext;
